import { useEffect } from "react";
import { useTranslation } from "@localization/useTranslation";
import { getEnumValues } from "@common/enumOperations";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useComplianceAreaQuery } from "@common/hooks/useSelectableItemQueries";
import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { SelectableColoredItem } from "../../processingActivity/ProcessingActivity.types";
import { AnnualWheelActivityGeneralType } from "../annualWheelDialog/AnnualWheelDialog.types";
import { ActivityFormProps } from "./ActivityForm.types";

export type ActivityCategorisationSectionProps = Pick<ActivityFormProps, "model" | "onModelChange" | "readOnly">;

export function useActivityCategorisationSection(props: ActivityCategorisationSectionProps) {
    const { translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();
    const { platformFeatures } = usePlanContext();
    const complianceAreasQuery = useComplianceAreaQuery();
    const businessAreasQuery = useDotLegalQueryWithKey<Array<SelectableColoredItem>>("selectableBusinessAreas", "/BusinessAreas/businessareas");
    const model = props.model;

    // A template names its framework rather than a compliance area, so the matching area is picked once the areas
    // are loaded. The framework is cleared afterwards so a later change of areas is not overwritten.
    useEffect(() => {
        if (complianceAreasQuery.data && platformFeatures.annualWheelComplianceArea) {
            const area = complianceAreasQuery.data.find((x) => x.framework === model.frameWork);

            if (area) {
                props.onModelChange({ areas: [area.id], frameWork: undefined });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complianceAreasQuery.data, model.frameWork]);

    const generalTypeOptions = getEnumValues(AnnualWheelActivityGeneralType)
        .filter((generalType) => generalType !== AnnualWheelActivityGeneralType.Obligation)
        .map((generalType) => ({ id: generalType.toString(), name: translateAnnualWheelActivityGeneralTypeEnum(generalType.toString()) }))
        .sort((a, b) => a.name.localeCompare(b.name));

    function onGeneralTypeChange(generalType: string | null) {
        props.onModelChange({ generalType: isNullOrWhitespace(generalType ?? undefined) ? undefined : Number(generalType) });
    }

    function onComplianceAreasChange(areas: Array<string>) {
        props.onModelChange({ areas });
    }

    function onTagsChange(tags: Array<string>) {
        props.onModelChange({ tags });
    }

    function onBusinessAreasChange(businessAreas: Array<string>) {
        props.onModelChange({ businessAreas });
    }

    return {
        generalTypeOptions,
        complianceAreas: complianceAreasQuery.data,
        complianceAreasLoading: complianceAreasQuery.isLoading,
        businessAreas: businessAreasQuery.data,
        businessAreasLoading: businessAreasQuery.isLoading,
        onGeneralTypeChange,
        onComplianceAreasChange,
        onTagsChange,
        onBusinessAreasChange,
    };
}
